import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Trash2, Edit, FileText } from 'lucide-react';
import { useMiscellaneousRegistryFilters } from './Filters/hooks/useMiscellaneousRegistryFilters';
import { MiscellaneousRegistryFilters } from './Filters/MiscellaneousRegistryFilters';
import type { DashboardProject } from '../../../types/project';
import { DocumentDownloadModal } from '../production/DocumentDownloadModal';
import {
  bulkDeleteRegistryMiscellaneous,
  deleteRegistryMiscellaneousDocumentsByDocId,
} from '../../../../../redux/Project/registrySaga';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import { AddMiscellaneousRegistryModal } from './AddMiscellaneousRegistryModal';
import { DeleteConfirmationDialog } from '../../../../../components/ui/DeleteConfirmationDialog';
import { fetchRegistryMiscellaneousData, refreshRegistryMiscellaneousData } from '../../../../../redux/Project/registryData/action';

export interface MiscellaneousRegistryTableHandle {
  openAdd: () => void;
}

interface MiscellaneousRegistryTableProps {
  project: DashboardProject;
  onRefresh?: () => void;
  canEdit?: boolean;
}

const cell = (row: any, ...keys: string[]) => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && v !== '') return String(v);
  }
  return '-';
};

const manufacturerCell = (row: any) => {
  const v = row?.manufacturer ?? row?.misc_extra_details?.manufacturer;
  if (v !== undefined && v !== null && v !== '') return String(v);
  return '-';
};

export const MiscellaneousRegistryTable = forwardRef<
  MiscellaneousRegistryTableHandle,
  MiscellaneousRegistryTableProps
>(function MiscellaneousRegistryTable({ project, onRefresh, canEdit = true }, ref) {
  const dispatch = useDispatch();
  const projectId = project?.project?.projectId;

  const registryData = useSelector((state: any) => state.registryData);
  const rows: any[] = useMemo(() => {
    if (!projectId) return [];
    const list = registryData?.registryMiscellaneousData?.[projectId];
    return Array.isArray(list) ? list : [];
  }, [registryData?.registryMiscellaneousData, projectId]);

  const loading = registryData?.registryMiscellaneousLoading || false;

  const {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueMaterialTypes,
    uniqueManufacturers,
    uniqueLocations,
    uniqueProductionTypes,
    filteredRows,
    hasActiveFilters,
    filteredCount,
    onResetFilters,
  } = useMiscellaneousRegistryFilters({ rows });

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editRow, setEditRow] = useState<any>(null);
  const [deleteRow, setDeleteRow] = useState<any>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [docDeleteOpen, setDocDeleteOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [docDeleteLoading, setDocDeleteLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    openAdd: () => {
      setEditRow(null);
      setIsAddOpen(true);
    },
  }));

  useEffect(() => {
    if (!projectId) return;
    const hasNoData = registryData?.registryMiscellaneousData?.[projectId] == null;
    if (hasNoData) {
      dispatch(fetchRegistryMiscellaneousData({ projectId }));
    }
  }, [projectId, registryData?.registryMiscellaneousData, dispatch]);

  const extractDocuments = (documents: any[]): any[] => {
    if (!documents?.length) return [];
    if (documents[0]?.materials && Array.isArray(documents[0].materials)) {
      return documents.flatMap((docGroup: any) =>
        (docGroup.materials || []).map((doc: any) => ({
          ...doc,
          docRef: docGroup.docRef,
        }))
      );
    }
    return documents;
  };

  const transformDocumentsForDownload = (documents: any[]) => {
    const flat = extractDocuments(documents || []);
    return flat.map((doc: any) => ({
      id: doc.registryMiscellaneousDocId || doc.id || doc.docId || Math.random().toString(),
      fileName: doc.fileName || doc.file_name,
      fileUrl: doc.fileUrl || doc.file_url,
      uploadDate: doc.uploadDate || doc.uploadedAt || doc.createdAt,
      uploadedBy: doc.uploadedBy || doc.createdBy,
      docId: doc.docId || doc.doc_id || doc.id,
    }));
  };

  const afterMutate = () => {
    if (projectId) {
      dispatch(refreshRegistryMiscellaneousData({ projectId }));
      dispatch(fetchRegistryMiscellaneousData({ projectId }));
    }
    onRefresh?.();
  };

  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    const res = await bulkDeleteRegistryMiscellaneous({ docIds: [deleteRow.id] });
    if (res) {
      setIsDeleteOpen(false);
      setDeleteRow(null);
      afterMutate();
    }
  };

  const handleDocDelete = async () => {
    if (!documentToDelete || !selectedRow) return;
    setDocDeleteLoading(true);
    try {
      const docId = documentToDelete.docId || documentToDelete.doc_id || documentToDelete.id;
      if (!docId) {
        customNotification.error('Delete failed', 'Document ID not found');
        return;
      }
      const ok = await deleteRegistryMiscellaneousDocumentsByDocId({ docIds: [Number(docId)] });
      if (ok) {
        setDocDeleteOpen(false);
        setIsDownloadOpen(false);
        setSelectedRow(null);
        setDocumentToDelete(null);
        afterMutate();
      }
    } finally {
      setDocDeleteLoading(false);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500 py-12">Loading miscellaneous registry...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <MiscellaneousRegistryFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFilterChange={onFilterChange}
          uniqueMaterialTypes={uniqueMaterialTypes}
          uniqueManufacturers={uniqueManufacturers}
          uniqueLocations={uniqueLocations}
          uniqueProductionTypes={uniqueProductionTypes}
          hasActiveFilters={hasActiveFilters}
          filteredCount={filteredCount}
          onResetFilters={onResetFilters}
        />

        <Card>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Material type</TableHead>
                    <TableHead>Part Number</TableHead>
                    <TableHead>Heat Number</TableHead>
                    <TableHead>Lot Number</TableHead>
                    <TableHead>Job Number</TableHead>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Size / weight</TableHead>
                    <TableHead>Manufacturer</TableHead>
                    <TableHead>Production</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-center w-[80px]">Documents</TableHead>
                    <TableHead className="text-center w-[100px] sticky right-0 z-10 bg-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center text-gray-500 py-10">
                        {rows.length === 0
                          ? 'No miscellaneous registry items yet. Use Add to create one.'
                          : hasActiveFilters
                            ? 'No rows match the current search or filters.'
                            : 'No rows to display.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row, index) => {
                      const docs = extractDocuments(row.documents || []);
                      return (
                        <TableRow key={row.id ?? index}>
                          <TableCell className="max-w-[140px] truncate" title={cell(row, 'misc_material_type_name', 'miscMaterialTypeName')}>
                            {cell(row, 'misc_material_type_name', 'miscMaterialTypeName')}
                          </TableCell>
                          <TableCell>
                            {cell(row, 'misc_part_number', 'part_number', 'partNumber')}
                          </TableCell>
                          <TableCell>
                            {cell(row, 'misc_heat_number', 'heat_number', 'heatNumber')}
                          </TableCell>
                          <TableCell>
                            {cell(row, 'misc_lot_number', 'lot_number', 'lotNumber')}
                          </TableCell>
                          <TableCell>
                            {cell(row, 'misc_job_number', 'job_number', 'jobNumber')}
                          </TableCell>
                          <TableCell>
                            {cell(row, 'misc_po_number', 'po_number', 'poNumber')}
                          </TableCell>
                          <TableCell>
                            {cell(row, 'misc_quantity', 'quantity', 'misc_quantity')}
                          </TableCell>
                          <TableCell className="max-w-[120px] truncate">
                            {cell(row, 'misc_size_or_weight', 'size_or_weight', 'sizeOrWeight')}
                          </TableCell>
                          <TableCell className="max-w-[140px] truncate">
                            {manufacturerCell(row)}
                          </TableCell>
                          <TableCell>
                            {cell(
                              row,
                              'misc_production_type',
                              'production_type',
                              'productionType'
                            )}
                          </TableCell>
                          <TableCell className="max-w-[120px] truncate">
                            {cell(row, 'misc_location', 'location')}
                          </TableCell>
                          <TableCell className="max-w-[160px] truncate">
                            {cell(row, 'misc_notes', 'notes')}
                          </TableCell>
                          <TableCell className="text-center">
                            {docs.length > 0 ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800"
                                title="View documents"
                                onClick={() => {
                                  setSelectedRow(row);
                                  setIsDownloadOpen(true);
                                }}
                              >
                                <FileText className="h-4 w-4" />
                                <span className="sr-only">{docs.length}</span>
                              </Button>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell className="text-center sticky right-0 z-10 bg-white">
                            <div className="flex justify-center gap-1">
                              {canEdit && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800"
                                    title="Edit"
                                    onClick={() => {
                                      setEditRow(row);
                                      setIsAddOpen(true);
                                    }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-red-600"
                                    title="Delete"
                                    onClick={() => {
                                      setDeleteRow(row);
                                      setIsDeleteOpen(true);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <AddMiscellaneousRegistryModal
        isOpen={isAddOpen}
        onClose={() => {
          setIsAddOpen(false);
          setEditRow(null);
        }}
        onSaved={afterMutate}
        project={project}
        itemToEdit={editRow || undefined}
      />

      <DeleteConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={handleDelete}
        itemName={deleteRow ? cell(deleteRow, 'misc_material_type_name', 'miscMaterialTypeName') : ''}
        itemType="miscellaneous registry item"
      />

      {selectedRow && extractDocuments(selectedRow.documents || []).length > 0 && (
        <DocumentDownloadModal
          isOpen={isDownloadOpen}
          onClose={() => {
            setIsDownloadOpen(false);
            setSelectedRow(null);
          }}
          documents={transformDocumentsForDownload(selectedRow.documents)}
          onDownload={(doc) => {
            const url = doc.fileUrl || (doc as any).file_url;
            if (url) window.open(url, '_blank');
          }}
          onDelete={(doc) => {
            setDocumentToDelete(doc);
            setDocDeleteOpen(true);
          }}
          title="Miscellaneous registry documents"
          canDelete={canEdit}
        />
      )}

      <DeleteConfirmationDialog
        isOpen={docDeleteOpen}
        onClose={() => {
          setDocDeleteOpen(false);
          setDocumentToDelete(null);
        }}
        onConfirm={handleDocDelete}
        isLoading={docDeleteLoading}
        itemToDelete={documentToDelete}
        itemType="Document"
        itemName={documentToDelete?.fileName || documentToDelete?.file_name || ''}
        title="Delete document"
      />
    </>
  );
});
